/**
 * Proposals Vuex Module
 *
 * Manages AI co-pilot proposals -- structured edit operations that are
 * reviewed before being applied to target records (e.g. lessons).
 *
 * Proposals are persisted on the backend and fetched per-target. The agent
 * data stream pushes real-time notifications when new proposals are created.
 */
import api from '@/factories/api';

const state = {
  proposalsByTarget: {},
  loading: false,
};

function targetKey(collection, targetId) {
  return `${collection}:${targetId}`;
}

const mutations = {
  setProposals(state, { collection, targetId, proposals }) {
    const key = targetKey(collection, targetId);
    state.proposalsByTarget = {
      ...state.proposalsByTarget,
      [key]: proposals,
    };
  },

  addProposal(state, { collection, targetId, proposal }) {
    const key = targetKey(collection, targetId);
    const existing = state.proposalsByTarget[key] || [];
    state.proposalsByTarget = {
      ...state.proposalsByTarget,
      [key]: [...existing, proposal],
    };
  },

  updateProposalStatus(state, { proposalId, status }) {
    for (const key of Object.keys(state.proposalsByTarget)) {
      const proposals = state.proposalsByTarget[key];
      const idx = proposals.findIndex((p) => p.id === proposalId);
      if (idx !== -1) {
        const updated = [...proposals];
        updated[idx] = { ...updated[idx], status };
        state.proposalsByTarget = {
          ...state.proposalsByTarget,
          [key]: updated,
        };
        break;
      }
    }
  },

  removeProposal(state, { proposalId }) {
    for (const key of Object.keys(state.proposalsByTarget)) {
      const proposals = state.proposalsByTarget[key];
      const filtered = proposals.filter((p) => p.id !== proposalId);
      if (filtered.length !== proposals.length) {
        state.proposalsByTarget = {
          ...state.proposalsByTarget,
          [key]: filtered,
        };
        break;
      }
    }
  },

  setLoading(state, loading) {
    state.loading = loading;
  },
};

const actions = {
  /**
   * Fetch pending proposals for a specific target document.
   */
  async fetchProposals(
    { commit },
    { collection, targetId, status = 'pending' }
  ) {
    commit('setLoading', true);
    try {
      const params = {
        target_collection: collection,
        target_id: targetId,
        status,
      };
      const response = await api().get('proposals', { params });
      const proposals = Array.isArray(response) ? response : [];
      commit('setProposals', { collection, targetId, proposals });
      return proposals;
    } catch (error) {
      console.error('proposals/fetchProposals error:', error);
      return [];
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Accept a proposal (marks as accepted on backend).
   * The caller is responsible for applying operations to their local state.
   */
  async acceptProposal({ commit }, proposalId) {
    try {
      await api().put(`proposals/${proposalId}`, { status: 'accepted' });
      commit('updateProposalStatus', { proposalId, status: 'accepted' });
      return true;
    } catch (error) {
      console.error('proposals/acceptProposal error:', error);
      return false;
    }
  },

  /**
   * Reject a proposal.
   */
  async rejectProposal({ commit }, proposalId) {
    try {
      await api().put(`proposals/${proposalId}`, { status: 'rejected' });
      commit('updateProposalStatus', { proposalId, status: 'rejected' });
      return true;
    } catch (error) {
      console.error('proposals/rejectProposal error:', error);
      return false;
    }
  },

  /**
   * Fetch a preview of what the target would look like with a proposal applied.
   */
  async previewProposal(_, proposalId) {
    try {
      const preview = await api().get(`proposals/${proposalId}/preview`);
      return preview || null;
    } catch (error) {
      console.error('proposals/previewProposal error:', error);
      return null;
    }
  },

  /**
   * Handle a real-time proposalCreated event from the agent data stream.
   * Fetches the full proposal from the API and adds it to state.
   */
  async onProposalCreated({ commit }, event) {
    const { proposalId, targetCollection, targetId } = event;
    try {
      const proposal = await api().get(`proposals/${proposalId}`);
      if (proposal && proposal.id) {
        commit('addProposal', {
          collection: targetCollection,
          targetId,
          proposal,
        });
      }
    } catch (error) {
      console.error('proposals/onProposalCreated error:', error);
    }
  },
};

const getters = {
  pendingProposals: (state) => (collection, targetId) => {
    const key = targetKey(collection, targetId);
    const proposals = state.proposalsByTarget[key] || [];
    return proposals.filter((p) => p.status === 'pending');
  },

  proposalCount: (state) => (collection, targetId) => {
    const key = targetKey(collection, targetId);
    const proposals = state.proposalsByTarget[key] || [];
    return proposals.filter((p) => p.status === 'pending').length;
  },

  isLoading: (state) => state.loading,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
